import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import BreadcrumbNav from '@/components/BreadcrumbNav';
import { 
  ArrowLeft, MapPin, Building, Calendar, Share2, Check, Eye, Target, TrendingUp,
  Phone, Mail, User, IndianRupee, Ruler, BadgeCheck, ChevronDown, ChevronUp,
  MessageCircle, Flame, Users, FileText, Zap, Sparkles, X, Home
} from 'lucide-react';
import { toast } from 'sonner';
import { MobileMenuTrigger } from '@/components/Layout';
import SmartShareModal from '@/components/SmartShareModal';

// Format price in L or Cr
const formatPrice = (lakh) => {
  if (!lakh || lakh === 0) return '—';
  if (lakh >= 100) return `₹${(lakh / 100).toFixed(lakh % 100 === 0 ? 0 : 1)}Cr`;
  return `₹${lakh}L`;
};

// Status badge colors
const getStatusBadge = (status) => {
  switch (status) {
    case 'Active': return { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' };
    case 'Paused': return { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' };
    case 'Sold Out': return { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200' };
    default: return { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200' };
  }
};

// SPOC type badge colors
const getSpocTypeBadge = (type) => {
  switch (type?.toLowerCase()) {
    case 'sales': return 'bg-blue-100 text-blue-700';
    case 'crm': return 'bg-purple-100 text-purple-700';
    case 'channel': return 'bg-emerald-100 text-emerald-700';
    case 'finance': return 'bg-amber-100 text-amber-700';
    case 'site': return 'bg-rose-100 text-rose-700';
    default: return 'bg-slate-100 text-slate-600';
  }
};

const ProjectDetail = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState([]);
  const [spocs, setSpocs] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [smartShareModalOpen, setSmartShareModalOpen] = useState(false);

  // Mock data for "Positioning Advantage" - would come from API
  const positioningAdvantages = [
    "Swift agreement execution – under 48 hours average turnaround",
    "Premium 2% brokerage structure for qualified deals",
    "Strong inventory availability across configurations",
    "Ideal for genuine end-use buyers seeking value"
  ];

  // City average conversion (mock - would come from API)
  const cityAvgConversion = 11;

  useEffect(() => {
    fetchProjectDetail();
  }, [projectId]);

  const fetchProjectDetail = async () => {
    setLoading(true);
    try {
      const [projectRes, pricingRes, spocsRes, activityRes] = await Promise.all([
        api.getProjectDetail(projectId),
        api.getProjectPricing(projectId).catch(() => ({ data: { pricing: [] } })),
        api.getProjectSpocs(projectId).catch(() => ({ data: { spocs: [] } })),
        api.getProjectTeamActivity(projectId, 30).catch(() => ({ data: { activity: [] } }))
      ]);
      
      setProject(projectRes.data);
      setPricing(pricingRes.data?.pricing || []);
      setSpocs(spocsRes.data?.spocs || []);
      setRecentActivity(activityRes.data?.activity || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Project not in your assigned city.');
        navigate('/projects');
      } else {
        toast.error('Failed to load project details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppShare = (type = 'pricing') => {
    if (!project) return;
    
    const repName = user?.rep_name || user?.email?.split('@')[0] || 'Your advisor';
    let message = '';
    
    if (type === 'pricing') {
      let priceDetails = '';
      if (pricing.length > 0) {
        priceDetails = pricing.map(p => `• ${p.configuration}: ${formatPrice(p.min_price_lakhs)} – ${formatPrice(p.max_price_lakhs)}`).join('\n');
      }
      message = `*${project.project_name}*\n📍 ${project.location}\n\n💰 *Configuration Pricing*\n${priceDetails || priceRange}\n\n– ${repName}`;
    } else if (type === 'snapshot') {
      const visits = project.fytd_visited_count || 0;
      const bookings = project.fytd_booked_count || 0;
      const conv = visits > 0 ? ((bookings / visits) * 100).toFixed(0) : 0;
      message = `*${project.project_name}*\n📍 ${project.location}\n💰 ${priceRange}\n\n📊 Performance:\n👀 ${visits} Visits | 🎯 ${bookings} Bookings | 📈 ${conv}% Conversion\n\n– ${repName}`;
    } else {
      message = `*${project.project_name}*\n📍 ${project.location}\n💰 ${priceRange}\n${project.possession ? `🏠 Possession: ${project.possession}` : ''}\n\nLet me know if you'd like to schedule a site visit.\n\n– ${repName}`;
    }

    const encodedMessage = encodeURIComponent(message.trim());
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    toast.success('Opening WhatsApp...');
    setShareMenuOpen(false);
  };

  const handleOpenSmartShare = () => {
    setSmartShareModalOpen(true);
    setShareMenuOpen(false);
  };

  const handleCallSpoc = (phone) => { window.location.href = `tel:${phone}`; };
  const handleWhatsAppSpoc = (phone) => { window.open(`https://wa.me/${phone.replace(/\D/g, '')}`, '_blank'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#EF4444] mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <Card className="p-8 text-center bg-white shadow-sm rounded-2xl">
          <p className="text-slate-500">Project not found</p>
          <Button onClick={() => navigate('/projects')} className="mt-4 bg-[#5B3DF5] hover:bg-[#4A2FE0]">Back to Projects</Button>
        </Card>
      </div>
    );
  }

  const statusStyle = getStatusBadge(project.status);
  
  // Calculate metrics
  const visits = project.fytd_visited_count || 0;
  const bookings = project.fytd_booked_count || 0;
  const conversionRate = visits > 0 ? ((bookings / visits) * 100).toFixed(0) : 0;
  const revenue = project.fytd_revenue || bookings * 15000000; // Mock if not available
  
  // Price range
  let priceRange = '—';
  if (pricing.length > 0) {
    const minPrices = pricing.filter(p => p.min_price_lakhs).map(p => p.min_price_lakhs);
    const maxPrices = pricing.filter(p => p.max_price_lakhs).map(p => p.max_price_lakhs);
    const minPrice = minPrices.length > 0 ? Math.min(...minPrices) : null;
    const maxPrice = maxPrices.length > 0 ? Math.max(...maxPrices) : null;
    if (minPrice && maxPrice) priceRange = `${formatPrice(minPrice)} – ${formatPrice(maxPrice)}`;
    else if (minPrice) priceRange = `From ${formatPrice(minPrice)}`;
  } else if (project.price_min_lakh && project.price_max_lakh) {
    priceRange = `${formatPrice(project.price_min_lakh)} – ${formatPrice(project.price_max_lakh)}`;
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav 
        items={[
          { label: 'Projects', path: '/projects' },
          { label: project.project_name }
        ]}
        fallbackPath="/projects"
      />
      
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-[52px] z-10 shadow-sm">
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-[#111827] truncate">{project.project_name}</h1>
            <div className="flex items-center gap-1">
              <MapPin size={10} className="text-slate-400" />
              <p className="text-xs text-slate-500 truncate">{project.location}</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
            {project.status}
          </span>
          <MobileMenuTrigger user={user} />
        </div>
      </div>

      <div className="p-4 space-y-3 max-w-2xl mx-auto">
        
        {/* 1. Performance Summary - TOP PRIORITY */}
        <Card data-testid="performance-summary" className="p-4 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl text-white">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} className="text-emerald-400" />
            <span className="text-xs font-semibold text-slate-300 uppercase tracking-wide">This Year Performance</span>
          </div>
          
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <p className="text-2xl font-black">{visits}</p>
              <p className="text-[10px] text-slate-400 uppercase">Visits</p>
            </div>
            <div>
              <p className="text-2xl font-black text-emerald-400">{bookings}</p>
              <p className="text-[10px] text-slate-400 uppercase">Bookings</p>
            </div>
            <div>
              <p className={`text-2xl font-black ${conversionRate >= 15 ? 'text-emerald-400' : conversionRate >= 8 ? 'text-amber-400' : 'text-red-400'}`}>
                {conversionRate}%
              </p>
              <p className="text-[10px] text-slate-400 uppercase">Conv</p>
            </div>
            <div>
              <p className="text-lg font-bold text-blue-400">{formatPrice(revenue / 100000)}</p>
              <p className="text-[10px] text-slate-400 uppercase">Revenue</p>
            </div>
          </div>
          
          {/* Conversion Bar */}
          <div className="mt-3 pt-3 border-t border-slate-700">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-slate-400">Conversion Rate</span>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-[10px]">City Avg: {cityAvgConversion}%</span>
                <span className={`font-bold ${conversionRate >= 15 ? 'text-emerald-400' : conversionRate >= 8 ? 'text-amber-400' : 'text-red-400'}`}>
                  {parseInt(conversionRate) > cityAvgConversion 
                    ? `↑ ${(conversionRate - cityAvgConversion).toFixed(0)}% above avg` 
                    : parseInt(conversionRate) < cityAvgConversion 
                      ? `↓ ${(cityAvgConversion - conversionRate).toFixed(0)}% below avg`
                      : 'At city avg'}
                </span>
              </div>
            </div>
            <div className="relative h-2.5 bg-slate-700 rounded-full overflow-hidden">
              {/* City average marker */}
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-slate-400 z-10"
                style={{ left: `${Math.min(cityAvgConversion * 3, 100)}%` }}
              ></div>
              <div 
                className={`h-full rounded-full transition-all duration-500 ${conversionRate >= 15 ? 'bg-emerald-500' : conversionRate >= 8 ? 'bg-amber-500' : 'bg-[#5B3DF5]'}`}
                style={{ width: `${Math.min(conversionRate * 3, 100)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-[9px] text-slate-500 mt-1">
              <span>0%</span>
              <span>15%</span>
              <span>30%+</span>
            </div>
          </div>
        </Card>

        {/* 2. Configuration Pricing - Accordion Style */}
        <Card data-testid="config-pricing" className="bg-white shadow-sm rounded-2xl overflow-hidden">
          <div className="p-3 flex items-center gap-2 border-b border-slate-100">
            <IndianRupee size={16} className="text-emerald-600" />
            <h3 className="font-bold text-sm text-[#111827]">Configuration Pricing</h3>
            <span className="text-[10px] text-slate-400 ml-auto">{priceRange}</span>
          </div>
          
          {pricing.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {pricing.map((config, index) => (
                <div key={index}>
                  <button 
                    onClick={() => setExpandedConfig(expandedConfig === index ? null : index)}
                    className="w-full p-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-slate-800">{config.configuration}</span>
                      {config.units_available && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                          {config.units_available} units
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#5B3DF5]">
                        {config.min_price_lakhs && config.max_price_lakhs
                          ? `${formatPrice(config.min_price_lakhs)} – ${formatPrice(config.max_price_lakhs)}`
                          : formatPrice(config.min_price_lakhs)}
                      </span>
                      {expandedConfig === index ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                    </div>
                  </button>
                  
                  {expandedConfig === index && (
                    <div className="px-3 pb-3 bg-slate-50">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {config.carpet_area_sqft && (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Ruler size={10} />
                            <span>Carpet: {config.carpet_area_sqft} sqft</span>
                          </div>
                        )}
                        {config.super_area_sqft && (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Ruler size={10} />
                            <span>Super: {config.super_area_sqft} sqft</span>
                          </div>
                        )}
                      </div>
                      {config.notes && <p className="text-[10px] text-slate-500 mt-2">{config.notes}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-slate-400">
              <IndianRupee size={20} className="mx-auto mb-2 opacity-50" />
              <p className="text-xs">No pricing details available</p>
            </div>
          )}
        </Card>

        {/* 3. Positioning Advantage */}
        <Card data-testid="positioning-advantage" className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 rounded-2xl shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🎯</span>
            <h3 className="font-bold text-sm text-amber-900">Positioning Advantage</h3>
          </div>
          <ul className="space-y-2.5">
            {positioningAdvantages.map((advantage, index) => (
              <li key={index} className="flex items-start gap-2.5 text-[13px] text-amber-800">
                <Check size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">{advantage}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* 4. SPOCs Section - Actionable */}
        <Card data-testid="project-spocs" className="bg-white shadow-sm rounded-2xl overflow-hidden">
          <div className="p-3 flex items-center gap-2 border-b border-slate-100">
            <User size={16} className="text-blue-600" />
            <h3 className="font-bold text-sm text-[#111827]">Project Contacts</h3>
          </div>
          
          {spocs.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {spocs.map((spoc, index) => (
                <div key={index} className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-800">{spoc.spoc_name}</span>
                        {spoc.is_primary && <BadgeCheck size={14} className="text-amber-500" />}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full capitalize ${getSpocTypeBadge(spoc.spoc_type)}`}>
                          {spoc.spoc_type}
                        </span>
                        {spoc.designation && <span className="text-[10px] text-slate-500">{spoc.designation}</span>}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {spoc.spoc_phone && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleCallSpoc(spoc.spoc_phone)} className="flex-1 h-8 text-xs rounded-lg">
                          <Phone size={12} className="mr-1 text-emerald-600" />
                          Call
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleWhatsAppSpoc(spoc.spoc_phone)} className="flex-1 h-8 text-xs rounded-lg">
                          <MessageCircle size={12} className="mr-1 text-green-600" />
                          WhatsApp
                        </Button>
                      </>
                    )}
                    {spoc.spoc_email && (
                      <Button size="sm" variant="outline" onClick={() => window.location.href = `mailto:${spoc.spoc_email}`} className="flex-1 h-8 text-xs rounded-lg">
                        <Mail size={12} className="mr-1 text-blue-600" />
                        Email
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center">
              <User size={20} className="mx-auto mb-2 text-slate-300" />
              <p className="text-xs text-slate-400 mb-3">No SPOC details available</p>
              <Button size="sm" variant="outline" className="text-xs rounded-lg">
                Add SPOC Contact
              </Button>
            </div>
          )}
        </Card>

        {/* 5. Recent Team Activity */}
        <Card data-testid="recent-activity" className="p-4 bg-white shadow-sm rounded-2xl">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-purple-600" />
            <h3 className="font-bold text-sm text-[#111827]">Recent Team Activity</h3>
            <span className="text-[10px] text-slate-400 ml-auto">Last 30 days</span>
          </div>
          {recentActivity.length > 0 ? (
            <div className="space-y-2.5">
              {recentActivity.map((activity, index) => (
                <div key={index} className="flex items-start justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-purple-700">
                        {activity.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium text-slate-800">{activity.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-700">{activity.action}</p>
                    <p className="text-[10px] text-slate-400">{activity.timeframe}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <Users size={20} className="mx-auto mb-2 text-slate-300" />
              <p className="text-xs text-slate-400">No recent team activity</p>
            </div>
          )}
        </Card>

        {/* 6. Project Details */}
        <Card data-testid="project-info" className="p-4 bg-white shadow-sm rounded-2xl">
          <div className="flex items-center gap-2 mb-3">
            <Building size={16} className="text-slate-600" />
            <h3 className="font-bold text-sm text-[#111827]">Project Details</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-slate-400 uppercase text-[10px]">Location</p>
              <p className="font-medium text-slate-700">{project.location}{project.city ? `, ${project.city}` : ''}</p>
            </div>
            {project.builder_name && (
              <div>
                <p className="text-slate-400 uppercase text-[10px]">Builder</p>
                <p className="font-medium text-slate-700">{project.builder_name}</p>
              </div>
            )}
            {project.configuration && (
              <div>
                <p className="text-slate-400 uppercase text-[10px]">Configuration</p>
                <p className="font-medium text-slate-700">{project.configuration}</p>
              </div>
            )}
            {project.possession && (
              <div>
                <p className="text-slate-400 uppercase text-[10px]">Possession</p>
                <p className="font-medium text-slate-700">{project.possession}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Highlights */}
        {project.highlights && project.highlights.length > 0 && (
          <Card data-testid="project-highlights" className="p-4 bg-white shadow-sm rounded-2xl">
            <h3 className="font-bold text-sm text-[#111827] mb-3">Key Highlights</h3>
            <ul className="space-y-1.5">
              {project.highlights.map((highlight, index) => (
                <li key={index} className="flex items-start gap-2 text-xs">
                  <Check size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{highlight}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {/* Smart Share Modal */}
      <SmartShareModal
        isOpen={smartShareModalOpen}
        onClose={() => setSmartShareModalOpen(false)}
        projectId={project?.project_id}
        projectName={project?.project_name}
        onShare={() => setSmartShareModalOpen(false)}
      />

      {/* Fixed Bottom Share Panel - Premium Design */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] z-20">
        <div className="max-w-2xl mx-auto p-3">
          {shareMenuOpen ? (
            <div className="space-y-3">
              {/* Primary Action - Smart Share with Customer */}
              <Button 
                data-testid="smart-share-btn"
                onClick={handleOpenSmartShare}
                className="w-full h-12 bg-gradient-to-r from-[#5B3DF5] to-[#7C5DFA] hover:from-[#4A2FE0] hover:to-[#6B4CE0] text-white font-bold rounded-xl shadow-md"
              >
                <Sparkles size={16} className="mr-2" />
                Share with Customer
              </Button>
              
              {/* Secondary Quick Share Options */}
              <div className="grid grid-cols-3 gap-2">
                <Button 
                  onClick={() => handleWhatsAppShare('pricing')} 
                  size="sm" 
                  variant="outline"
                  className="h-10 text-xs rounded-xl border-slate-200 hover:bg-slate-50 flex flex-col items-center justify-center gap-0.5 py-2"
                >
                  <IndianRupee size={14} className="text-emerald-600" />
                  <span className="font-medium">Pricing</span>
                </Button>
                <Button 
                  onClick={() => handleWhatsAppShare('snapshot')} 
                  size="sm" 
                  variant="outline"
                  className="h-10 text-xs rounded-xl border-slate-200 hover:bg-slate-50 flex flex-col items-center justify-center gap-0.5 py-2"
                >
                  <TrendingUp size={14} className="text-blue-600" />
                  <span className="font-medium">Stats</span>
                </Button>
                <Button 
                  onClick={() => handleWhatsAppShare('brochure')} 
                  size="sm" 
                  variant="outline"
                  className="h-10 text-xs rounded-xl border-slate-200 hover:bg-slate-50 flex flex-col items-center justify-center gap-0.5 py-2"
                >
                  <FileText size={14} className="text-purple-600" />
                  <span className="font-medium">Details</span>
                </Button>
              </div>
              
              <Button 
                onClick={() => setShareMenuOpen(false)} 
                variant="ghost" 
                className="w-full h-9 text-xs text-slate-500 rounded-xl"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                data-testid="share-whatsapp"
                onClick={() => setShareMenuOpen(true)}
                className="flex-1 h-12 bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold rounded-xl shadow-md"
              >
                <Share2 size={16} className="mr-2" />
                Share Project
              </Button>
              <Button
                data-testid="ask-saira-project"
                onClick={() => navigate('/saira', { state: { projectContext: project.project_name, projectId: project.project_id } })}
                className="h-12 px-4 bg-[#5B3DF5] hover:bg-[#4A2FE0] text-white font-bold rounded-xl shadow-md"
              >
                <MessageCircle size={16} />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
